import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import RttRoundedIcon from '@mui/icons-material/RttRounded';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { useTranslation } from 'react-i18next';

import CommonModal from '../../../components/common-modal/CommonModal';
import ContentSlider from '../../components/content-slider/ContentSlider';
import { selectActiv8BucketById } from '../../../redux/activ8/activ8Selectors';
import ContentSlide from '../../components/content-slide/ContentSlide';
import TagList from '../../components/tag-list/TagList';
import CommonButton, { BUTTON_STYLES } from '../../../components/common-button/CommonButton';
import Icon from '../../../icons/Icon';
import IsGranted from '../../../components/is-granted/IsGranted';
import { ACTIV8_CONTENT_STATUS, USER_ROLES } from '../../../constants';
import PublicLinks from './public-links/PublicLinks';
import UploadedBy from './uploaded-by/UploadedBy';
import ThirdPartyRightsBadge from './third-party-rights-badge/ThirdPartyRightsBadge';
import EditTagsModal from '../../components/edit-tags-modal/EditTagsModal';
import useCommonModal from '../../../hooks/useCommonModal';
import ExpiredBadge from '../content-list/card/expired-badge/ExpiredBadge';
import { MEDIA_LINK_VARIANTS } from '../../utils/resolveActiv8MediaLink';
import { clampMediaIndex } from '../../utils/mediaIndex';

import scss from './ContentModal.module.scss';

function ContentModal({
  isOpen,
  onClose,
  initialIndex,
  bucketId,
  onLike,
  onDownload,
  onDownloadSource,
  onStudio,
  onDelete,
  onStory,
  onRestore,
  selectBucketById = selectActiv8BucketById,
}) {
  return (
    <CommonModal isOpen={isOpen} onClose={onClose}>
      <ContentModalBody
        initialIndex={initialIndex}
        bucketId={bucketId}
        onClose={onClose}
        onLike={onLike}
        onDownload={onDownload}
        onDownloadSource={onDownloadSource}
        onStudio={onStudio}
        onDelete={onDelete}
        onStory={onStory}
        onRestore={onRestore}
        selectBucketById={selectBucketById}
      />
    </CommonModal>
  );
}

function ContentModalBody({
  initialIndex,
  bucketId,
  onClose,
  onLike,
  onDownload,
  onDownloadSource,
  onStudio,
  onDelete,
  onStory,
  onRestore,
  selectBucketById,
}) {
  const { t } = useTranslation('activ8');
  const { media } = useSelector((state) => selectBucketById(state, bucketId));
  const mediaItems = useMemo(() => media ?? [], [media]);

  const [slideIndex, setSlideIndex] = useState(() => clampMediaIndex(initialIndex, mediaItems.length));

  useEffect(() => {
    if (!mediaItems.length) {
      onClose();
      return;
    }

    setSlideIndex((index) => clampMediaIndex(index, mediaItems.length));
  }, [mediaItems.length, onClose]);

  const safeIndex = clampMediaIndex(slideIndex, mediaItems.length);
  const selectedSlide = mediaItems[safeIndex];
  const isExpired = selectedSlide?.status === ACTIV8_CONTENT_STATUS.EXPIRED;
  const {
    isOpen: isEditTagsModalOpen,
    handleOpenModal: handleOpenEditTagsModal,
    handleCloseModal: handleCloseEditTagsModal,
  } = useCommonModal();

  const handleSwipe = (_, next) => {
    setSlideIndex(next);
  };

  const handleOpenStudio = () => {
    onStudio({ media: mediaItems, slideIndex: safeIndex, bucketId });
  };

  const handleOpenStory = () => {
    onStory(selectedSlide);
  };

  const handleRestore = () => {
    onRestore(selectedSlide);
  };

  const enableEditTags = Boolean(onDelete || onLike);

  const slideProps = isExpired
    ? {
        className: scss.slide,
        popperZIndex: 9001,
        disableMoreActionsPortal: true,
        mediaLinkVariant: MEDIA_LINK_VARIANTS.THUMBNAIL,
      }
    : {
        className: scss.slide,
        popperZIndex: 9001,
        disableMoreActionsPortal: true,
        mediaLinkVariant: MEDIA_LINK_VARIANTS.DISPLAY,
        onLike,
        onDownload,
        onDownloadSource,
        onEditTags: enableEditTags ? handleOpenEditTagsModal : undefined,
        onDelete,
        onRestore,
      };

  return (
    <>
      <div className={scss.wrapper}>
        <div className={scss.sliderWrapper}>
          {isExpired ? <ExpiredBadge /> : null}
          <ContentSlider
            slides={mediaItems}
            Slide={ContentSlide}
            contrastedArrows
            slideProps={slideProps}
            index={safeIndex}
            onSwipe={handleSwipe}
          />
        </div>

        <div className={clsx(scss.tags, isExpired && scss.expiredContent)}>
          <TagList tags={selectedSlide?.tags} isExpandable />
        </div>
        <div className={scss.rightsInfo}>
          <ThirdPartyRightsBadge thirdPartyRights={selectedSlide?.thirdPartyRights} />
          <UploadedBy name={selectedSlide?.name} username={selectedSlide?.username} />
        </div>

        <div className={scss.actions}>
          <div className={scss.actionsBlock}>
            {!isExpired && onStudio ? (
              <IsGranted permissions={[USER_ROLES.GLOBAL_ADMIN]}>
                <PublicLinks
                  displayLink={selectedSlide?.contentDisplayLink}
                  downloadLink={selectedSlide?.contentDownloadLink}
                />
              </IsGranted>
            ) : null}
          </div>

          <div className={scss.actionsBlock}>
            {onStudio ? (
              <CommonButton
                styleColor={BUTTON_STYLES.PRIMARY}
                maxWidth={160}
                onClick={handleOpenStudio}
                disabled={isExpired}>
                <Icon icon={AutoFixHighOutlinedIcon} />
                {t('goToStudio')}
              </CommonButton>
            ) : null}

            {onStory ? (
              <CommonButton
                styleColor={BUTTON_STYLES.PRIMARY}
                maxWidth={160}
                onClick={handleOpenStory}
                disabled={isExpired}>
                <Icon icon={RttRoundedIcon} />
                {t('goToStory')}
              </CommonButton>
            ) : null}

            {onRestore ? (
              <CommonButton styleColor={BUTTON_STYLES.PRIMARY} maxWidth={160} onClick={handleRestore}>
                <Icon icon={ReplayOutlinedIcon} />
                {t('restore')}
              </CommonButton>
            ) : null}
          </div>
        </div>
      </div>
      {enableEditTags ? (
        <EditTagsModal
          isOpen={isEditTagsModalOpen}
          onClose={handleCloseEditTagsModal}
          tags={selectedSlide?.tags}
          contentId={selectedSlide?.id}
        />
      ) : null}
    </>
  );
}

export default ContentModal;
